/** Настройки игрока.
 *
 * Три переключателя из дизайна — порядок ввода, автоочистка заметок и
 * тактильный отклик — плюс то, что нельзя не дать: звук, уведомления бота
 * и язык.
 *
 * Первые три и уведомления живут на сервере, а не только в CloudStorage:
 * автоочистка меняет поведение API, язык — язык объяснений подсказок,
 * которые собираются на сервере, уведомления — рассылку бота. Хранить это
 * на клиенте значит завести две правды об одном игроке. Звук остаётся
 * местным: он свойство устройства, а не человека.
 */

import { useEffect, useState } from 'react'

import { saveSettings } from '../api/endpoints'
import type { User } from '../api/types'
import { Switch } from '../components/ui'
import { getSettings, updateSettings } from '../game/settings'
import { play } from '../game/sound'
import { type Language, t } from '../i18n'
import { backButton } from '../telegram/webapp'

export interface SettingsScreenProps {
  user: User
  techniques: { discovered: number; total: number }
  onExit: () => void
  onUserChange: (user: User) => void
  /** Смена языка перерисовывает дерево целиком: строки разбросаны везде. */
  onLanguageChange: () => void
  onOpenTechniques: () => void
  onInvite: () => void
}

export function SettingsScreen({
  user,
  techniques,
  onExit,
  onUserChange,
  onLanguageChange,
  onOpenTechniques,
  onInvite,
}: SettingsScreenProps) {
  const [local, setLocal] = useState(getSettings())
  const [busy, setBusy] = useState(false)

  useEffect(() => backButton.show(onExit), [onExit])

  const patch = async (changes: Parameters<typeof saveSettings>[0]) => {
    setBusy(true)
    try {
      const next = await saveSettings(changes)
      // Тактильный отклик спрашивают из кода, которому недоступен профиль
      // (`telegram/webapp.ts`), поэтому его значение дублируется в местные
      // настройки. Источник истины при этом один — сервер.
      updateSettings({ haptics: next.haptics_enabled })
      setLocal(getSettings())
      onUserChange(next)
    } finally {
      setBusy(false)
    }
  }

  const switchLanguage = async (language: Language) => {
    updateSettings({ language })
    setLocal(getSettings())
    await patch({ language_code: language })
    onLanguageChange()
  }

  return (
    <div className="screen">
      <header className="topbar">
        <button type="button" className="topbar__action" onClick={onExit} aria-label={t('app.back')}>
          ‹
        </button>
        <span className="topbar__title">{t('settings.title')}</span>
        <span className="topbar__spacer" />
      </header>

      <div className="screen__body">
        <div className="list">
          <Row
            title={t('settings.digitFirst')}
            hint={t('settings.digitFirstHint')}
            on={user.digit_first}
            busy={busy}
            onToggle={() => void patch({ digit_first: !user.digit_first })}
          />
          <Row
            title={t('settings.autoClear')}
            hint={t('settings.autoClearHint')}
            on={user.auto_clear_notes}
            busy={busy}
            onToggle={() => void patch({ auto_clear_notes: !user.auto_clear_notes })}
          />
          <Row
            title={t('settings.haptics')}
            hint={t('settings.hapticsHint')}
            on={user.haptics_enabled}
            busy={busy}
            onToggle={() => void patch({ haptics_enabled: !user.haptics_enabled })}
          />
        </div>

        <div className="list">
          <Row
            title={t('settings.sound')}
            hint={t('settings.soundHint')}
            on={local.sound}
            busy={false}
            onToggle={() => {
              const next = updateSettings({ sound: !local.sound })
              setLocal({ ...next })
              if (next.sound) play('place')
            }}
          />
          <Row
            title={t('settings.notifications')}
            hint={t('settings.notificationsHint')}
            on={user.notifications_enabled}
            busy={busy}
            onToggle={() =>
              void patch({ notifications_enabled: !user.notifications_enabled })
            }
          />
        </div>

        <div className="list">
          <button type="button" className="list__row" onClick={onOpenTechniques}>
            <span className="list__grow">
              <span className="profile__name" style={{ display: 'block' }}>
                {t('settings.techniques')}
              </span>
              <span className="hint-text">
                {t('settings.techniquesHint', {
                  done: techniques.discovered,
                  total: techniques.total,
                })}
              </span>
            </span>
            <span className="topbar__action" style={{ width: 'auto' }}>
              ›
            </span>
          </button>
          <button type="button" className="list__row" onClick={onInvite}>
            <span className="list__grow">
              <span className="profile__name" style={{ display: 'block' }}>
                {t('settings.invite')}
              </span>
              <span className="hint-text">{t('settings.inviteHint')}</span>
            </span>
            <span className="topbar__action" style={{ width: 'auto' }}>
              ›
            </span>
          </button>
        </div>

        <div className="stack stack--tight">
          <span className="section-label">{t('settings.language')}</span>
          <div className="segmented">
            {(['ru', 'en'] as Language[]).map((language) => (
              <button
                key={language}
                type="button"
                className={`segmented__item ${
                  local.language === language ? 'segmented__item--active' : ''
                }`.trim()}
                onClick={() => void switchLanguage(language)}
                disabled={busy}
              >
                {language === 'ru' ? 'Русский' : 'English'}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function Row({
  title,
  hint,
  on,
  busy,
  onToggle,
}: {
  title: string
  hint: string
  on: boolean
  busy: boolean
  onToggle: () => void
}) {
  return (
    <div className="list__row">
      <span className="list__grow">
        <span className="profile__name" style={{ display: 'block' }}>
          {title}
        </span>
        <span className="hint-text">{hint}</span>
      </span>
      <Switch on={on} onToggle={busy ? () => {} : onToggle} label={title} />
    </div>
  )
}
